import React from 'react';
import { Box, Button, Grid, Typography, makeStyles } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import VpnLockIcon from '@material-ui/icons/VpnLock';
import BarChartIcon from '@material-ui/icons/BarChart';
import DashboardIcon from '@material-ui/icons/Dashboard';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import CloudIcon from '@material-ui/icons/Cloud';
import ChatIcon from '@material-ui/icons/Chat';
import BuildIcon from '@material-ui/icons/Build';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import CloudQueueIcon from '@material-ui/icons/CloudQueue';
import SmartToyIcon from '@material-ui/icons/EmojiObjects';
import SettingsIcon from '@material-ui/icons/Settings';
import VisibilityIcon from '@material-ui/icons/Visibility';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import PublishIcon from '@material-ui/icons/Publish';
import { Content, Page } from '@backstage/core-components';

const BISHOP_URL = 'https://bishop.epsilon.com';

const FOUNDATION_ITEMS = [
  {
    icon: CloudQueueIcon,
    num: '01',
    title: 'Provisioning',
    description:
      'Software Templates (Scaffolder) run multi-step workflows to provision infrastructure from standardized configurations.',
  },
  {
    icon: SmartToyIcon,
    num: '02',
    title: 'AI-Assisted Provisioning',
    description:
      'Agent Chat guides users through template provisioning conversationally as an alternative to manual forms.',
  },
  {
    icon: SettingsIcon,
    num: '03',
    title: 'Patching & Operations',
    description:
      'Custom actions and plugins expose operational tasks in the Scaffolder or as dedicated buttons in the Catalog.',
  },
  {
    icon: VisibilityIcon,
    num: '04',
    title: 'Monitoring',
    description:
      "Custom plugins surface monitoring data directly on a component's Catalog page.",
  },
];

const CAPABILITIES = [
  {
    icon: BarChartIcon,
    title: 'Insights',
    items: [
      'Infrastructure usage analytics and provisioning trends',
      'Status breakdown (completed, processing, failed) for the last 30 days',
      'Top templates used and user distribution',
    ],
  },
  {
    icon: DashboardIcon,
    title: 'Task Dashboard',
    items: [
      'Track all provisioning and operational tasks',
      'Status indicators and detailed task views by Run ID',
    ],
  },
  {
    icon: AutorenewIcon,
    title: 'Lifecycle Management',
    items: ['Manage infrastructure component lifecycles end to end'],
  },
  {
    icon: CloudIcon,
    title: 'Infra Provisioning',
    items: [
      'Self-service provisioning via Scaffolder templates',
      'Multi-cloud support: AWS, Azure, GCP, and On-Premises',
    ],
  },
  {
    icon: ChatIcon,
    title: 'Create with Agent',
    badge: 'BETA',
    items: [
      'Conversational alternative to filling out provisioning forms manually',
      'Available on template cards that support agent-assisted creation',
    ],
  },
  {
    icon: BuildIcon,
    title: 'Infra Operations & Maintenance',
    items: [
      'Patch Management — Trigger patching workflows',
      'Change Management — Track and execute changes',
      'Linux / Windows — OS-specific operational tasks',
      'Executions — View and manage execution history',
    ],
  },
  {
    icon: PlayArrowIcon,
    title: 'Playground',
    items: ['Test and experiment with templates and workflows'],
  },
  {
    icon: AddCircleOutlineIcon,
    title: 'Create New Component',
    items: ['Register new infrastructure components in the catalog'],
  },
  {
    icon: MenuBookIcon,
    title: 'Docs',
    items: ['Markdown docs from code repos rendered directly in the portal'],
  },
];

const ACCESS_GROUPS = [
  {
    group: 'AICOE_STS_BUILD',
    label: 'Build',
    description: 'Provisioning templates and build workflows',
  },
  {
    group: 'AICOE_STS_OPS',
    label: 'Ops',
    description: 'Operational tasks, patching, and monitoring',
  },
  {
    group: 'AICOE_STS_ADMIN',
    label: 'Admin',
    description: 'Full access, including admin functions and approvals',
  },
];

const useStyles = makeStyles(theme => ({
  // ─── Keyframes ────────────────────────────────────────────────────────────
  '@keyframes floatA': {
    '0%, 100%': { transform: 'translateY(0px) scale(1)', opacity: 0.6 },
    '50%': { transform: 'translateY(-28px) scale(1.1)', opacity: 0.9 },
  },
  '@keyframes floatB': {
    '0%, 100%': { transform: 'translateY(0px) scale(1)', opacity: 0.35 },
    '50%': { transform: 'translateY(22px) scale(1.08)', opacity: 0.6 },
  },
  '@keyframes pulseDot': {
    '0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(96,165,250,0.6)' },
    '50%': { transform: 'scale(1.25)', boxShadow: '0 0 0 6px rgba(96,165,250,0)' },
  },
  '@keyframes gradientShift': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },

  // ─── Hero ─────────────────────────────────────────────────────────────────
  hero: {
    position: 'relative',
    background: '#07091a',
    borderRadius: 20,
    padding: theme.spacing(9, 7, 8),
    marginBottom: theme.spacing(8),
    overflow: 'hidden',
    // subtle inset blue border
    boxShadow: 'inset 0 0 0 1px rgba(37,99,235,0.18)',
    // fine grid texture
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: [
        'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
        'linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
      ].join(','),
      backgroundSize: '52px 52px',
      pointerEvents: 'none',
    },
    // glowing bottom-edge line
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '8%',
      right: '8%',
      height: 1,
      background:
        'linear-gradient(90deg, transparent 0%, rgba(37,99,235,0.55) 40%, rgba(99,102,241,0.55) 60%, transparent 100%)',
      pointerEvents: 'none',
    },
  },
  heroOrb1: {
    position: 'absolute',
    top: '-15%',
    right: '2%',
    width: 580,
    height: 580,
    borderRadius: '50%',
    background:
      'radial-gradient(circle, rgba(37,99,235,0.28) 0%, transparent 65%)',
    animation: '$floatA 7s ease-in-out infinite',
    pointerEvents: 'none',
  },
  heroOrb2: {
    position: 'absolute',
    bottom: '-20%',
    left: '10%',
    width: 400,
    height: 400,
    borderRadius: '50%',
    background:
      'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 68%)',
    animation: '$floatB 9s ease-in-out infinite',
    pointerEvents: 'none',
  },
  heroWatermark: {
    position: 'absolute',
    bottom: -28,
    right: -20,
    fontSize: '15rem',
    fontWeight: 900,
    letterSpacing: '-0.06em',
    lineHeight: 1,
    // gradient watermark instead of flat opacity
    background:
      'linear-gradient(135deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.012) 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    userSelect: 'none',
    pointerEvents: 'none',
    zIndex: 0,
  },
  heroInner: {
    position: 'relative',
    zIndex: 1,
  },
  heroPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(37,99,235,0.12)',
    border: '1px solid rgba(37,99,235,0.32)',
    borderRadius: 100,
    padding: '5px 14px',
    marginBottom: theme.spacing(3.5),
  },
  heroPillDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#60a5fa',
    flexShrink: 0,
    animation: '$pulseDot 2.2s ease-in-out infinite',
  },
  heroPillText: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    // high-contrast light blue on dark
    color: '#bfdbfe',
  },
  // gradient animated BISHOP title
  heroTitle: {
    fontSize: '5.75rem',
    fontWeight: 900,
    letterSpacing: '-0.05em',
    lineHeight: 0.88,
    marginBottom: theme.spacing(3),
    backgroundSize: '200% auto',
    background:
      'linear-gradient(115deg, #ffffff 0%, #93c5fd 30%, #c7d2fe 55%, #ffffff 80%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    animation: '$gradientShift 5s ease infinite',
  },
  heroTagline: {
    fontFamily: '"Courier New", Courier, monospace',
    fontSize: '0.83rem',
    fontWeight: 400,
    // boosted contrast from prev 0.35
    color: 'rgba(255,255,255,0.68)',
    letterSpacing: '0.04em',
    marginBottom: theme.spacing(4),
    maxWidth: 480,
  },
  heroBody: {
    fontSize: '1.05rem',
    lineHeight: 1.8,
    // boosted contrast from prev 0.62
    color: 'rgba(255,255,255,0.88)',
    maxWidth: 500,
    marginBottom: theme.spacing(5),
  },
  heroCtaWrap: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
  },
  heroCta: {
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '0.9rem',
    textTransform: 'none',
    letterSpacing: '0.01em',
    padding: theme.spacing(1.5, 3.5),
    borderRadius: 10,
    boxShadow: '0 4px 18px rgba(37,99,235,0.45)',
    transition: 'background-color 0.2s, box-shadow 0.2s, transform 0.2s',
    '&:hover': {
      backgroundColor: '#1d4ed8',
      // ring effect on hover
      boxShadow:
        '0 0 0 4px rgba(37,99,235,0.25), 0 8px 28px rgba(37,99,235,0.55)',
      transform: 'translateY(-2px)',
    },
  },
  heroCtaNote: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: 'rgba(255,255,255,0.62)',
  },

  // ─── Section chrome ───────────────────────────────────────────────────────
  section: {
    marginBottom: theme.spacing(8),
  },
  sectionTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginBottom: theme.spacing(4),
  },
  sectionLabel: {
    fontSize: '0.68rem',
    fontWeight: 800,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    // readable against white page
    color: '#64748b',
    whiteSpace: 'nowrap',
  },
  sectionRule: {
    flex: 1,
    height: 1,
    background:
      'linear-gradient(90deg, #cbd5e1 0%, transparent 100%)',
  },

  // ─── Foundation cards ─────────────────────────────────────────────────────
  foundationCard: {
    position: 'relative',
    height: '100%',
    padding: theme.spacing(3.5),
    borderRadius: 14,
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    transition: 'border-color 0.25s, box-shadow 0.25s, transform 0.25s',
    // shimmer sweep layer
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-80%',
      width: '55%',
      height: '100%',
      background:
        'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
      transform: 'skewX(-18deg)',
      transition: 'left 0s',
      pointerEvents: 'none',
    },
    '&:hover': {
      borderColor: 'rgba(37,99,235,0.3)',
      // blue glow ring
      boxShadow:
        '0 0 0 1px rgba(37,99,235,0.15), 0 4px 8px rgba(0,0,0,0.04), 0 12px 28px rgba(37,99,235,0.08)',
      transform: 'translateY(-4px)',
    },
    '&:hover::after': {
      left: '160%',
      transition: 'left 0.65s ease',
    },
  },
  foundationGhostNum: {
    position: 'absolute',
    top: -16,
    right: 10,
    fontSize: '6.5rem',
    fontWeight: 900,
    color: '#0f172a',
    opacity: 0.04,
    lineHeight: 1,
    letterSpacing: '-0.04em',
    userSelect: 'none',
    pointerEvents: 'none',
    transition: 'opacity 0.3s',
  },
  foundationIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2.5),
    '& svg': { fontSize: 23, color: '#1e293b' },
  },
  foundationTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    // high-contrast near-black
    color: '#0f172a',
    marginBottom: theme.spacing(1),
    letterSpacing: '-0.01em',
  },
  foundationDesc: {
    fontSize: '0.875rem',
    lineHeight: 1.7,
    // darkened from #64748b for better readability
    color: '#475569',
  },

  // ─── Capability cards ─────────────────────────────────────────────────────
  capabilityCard: {
    position: 'relative',
    height: '100%',
    padding: theme.spacing(3),
    borderRadius: 14,
    border: '1px solid #e2e8f0',
    borderTop: '3px solid transparent',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    transition: 'border-top-color 0.25s, border-color 0.25s, box-shadow 0.25s, transform 0.25s',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-80%',
      width: '55%',
      height: '100%',
      background:
        'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
      transform: 'skewX(-18deg)',
      transition: 'left 0s',
      pointerEvents: 'none',
    },
    '&:hover': {
      borderTopColor: '#2563eb',
      borderColor: 'rgba(37,99,235,0.25)',
      boxShadow:
        '0 0 0 0 transparent, 0 4px 8px rgba(0,0,0,0.04), 0 12px 28px rgba(37,99,235,0.08)',
      transform: 'translateY(-4px)',
    },
    '&:hover::after': {
      left: '160%',
      transition: 'left 0.65s ease',
    },
  },
  capabilityHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderBottom: '1px solid #f1f5f9',
  },
  capabilityIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 9,
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': { fontSize: 20, color: '#1e293b' },
  },
  capabilityTitle: {
    fontWeight: 700,
    fontSize: '0.9rem',
    // high-contrast
    color: '#0f172a',
    flex: 1,
    lineHeight: 1.35,
    letterSpacing: '-0.01em',
  },
  badge: {
    display: 'inline-block',
    fontSize: '0.58rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    color: '#1d4ed8',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    padding: '2px 7px',
    borderRadius: 5,
    marginLeft: 7,
    verticalAlign: 'middle',
  },
  capabilityList: {
    margin: 0,
    paddingLeft: theme.spacing(2.25),
    '& li': {
      fontSize: '0.825rem',
      lineHeight: 1.75,
      // darkened for contrast
      color: '#475569',
      '& + li': {
        marginTop: theme.spacing(0.5),
      },
    },
  },

  // ─── Access cards ─────────────────────────────────────────────────────────
  accessNote: {
    fontSize: '0.875rem',
    color: '#64748b',
    marginBottom: theme.spacing(3),
  },
  accessCard: {
    position: 'relative',
    height: '100%',
    borderRadius: 14,
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    transition: 'border-color 0.25s, box-shadow 0.25s, transform 0.25s',
    '&:hover': {
      borderColor: 'rgba(37,99,235,0.3)',
      boxShadow:
        '0 0 0 1px rgba(37,99,235,0.12), 0 4px 8px rgba(0,0,0,0.04), 0 12px 28px rgba(37,99,235,0.08)',
      transform: 'translateY(-4px)',
    },
  },
  accessCardTop: {
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    padding: theme.spacing(3, 3, 2.5),
    borderBottom: '1px solid #e2e8f0',
  },
  accessCardBottom: {
    padding: theme.spacing(2.5, 3),
  },
  accessRole: {
    fontSize: '2.75rem',
    fontWeight: 900,
    // high contrast dark
    color: '#0f172a',
    letterSpacing: '-0.04em',
    lineHeight: 1,
    marginBottom: theme.spacing(1.5),
  },
  accessGroupCode: {
    display: 'inline-block',
    fontFamily: '"Courier New", Courier, monospace',
    fontSize: '0.73rem',
    // good contrast: dark text on white chip
    color: '#1e293b',
    backgroundColor: '#ffffff',
    border: '1px solid #cbd5e1',
    padding: '3px 10px',
    borderRadius: 5,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  accessDesc: {
    fontSize: '0.875rem',
    lineHeight: 1.65,
    // darkened for readability
    color: '#475569',
  },

  // ─── Getting started — dark echo of hero ─────────────────────────────────
  footerCard: {
    position: 'relative',
    height: '100%',
    backgroundColor: '#07091a',
    borderRadius: 14,
    border: '1px solid rgba(37,99,235,0.18)',
    padding: theme.spacing(4),
    overflow: 'hidden',
    display: 'flex',
    gap: theme.spacing(2.5),
    alignItems: 'flex-start',
    transition: 'transform 0.25s, box-shadow 0.25s, border-color 0.25s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 0 0 1px rgba(37,99,235,0.35), 0 16px 40px rgba(7,9,26,0.35)',
      borderColor: 'rgba(37,99,235,0.4)',
    },
    // grid texture
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: [
        'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px)',
        'linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
      ].join(','),
      backgroundSize: '44px 44px',
      pointerEvents: 'none',
    },
    // glow orb
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '-50%',
      right: '-15%',
      width: 260,
      height: 260,
      borderRadius: '50%',
      background:
        'radial-gradient(circle, rgba(37,99,235,0.22) 0%, transparent 70%)',
      pointerEvents: 'none',
    },
  },
  footerIconWrap: {
    position: 'relative',
    zIndex: 1,
    width: 46,
    height: 46,
    borderRadius: 11,
    backgroundColor: 'rgba(37,99,235,0.18)',
    border: '1px solid rgba(37,99,235,0.38)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': { fontSize: 22, color: '#93c5fd' },
  },
  footerContent: {
    position: 'relative',
    zIndex: 1,
  },
  footerTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    // full white for maximum contrast on dark bg
    color: '#ffffff',
    marginBottom: theme.spacing(0.75),
    letterSpacing: '-0.01em',
  },
  footerText: {
    fontSize: '0.875rem',
    lineHeight: 1.75,
    // boosted from 0.52 — now proper readable contrast
    color: 'rgba(255,255,255,0.78)',
    '& strong': {
      color: '#ffffff',
      fontWeight: 600,
    },
    '& code': {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '0.82rem',
      // bright on dark for code
      color: '#93c5fd',
      backgroundColor: 'rgba(37,99,235,0.18)',
      padding: '1px 6px',
      borderRadius: 4,
      border: '1px solid rgba(37,99,235,0.28)',
    },
  },
}));

export const BishopMigrationPage = () => {
  const classes = useStyles();

  return (
    <Page themeId="tool">
      <Content>

        {/* ── Hero ──────────────────────────────────────────────────── */}
        <Box className={classes.hero}>
          <Box className={classes.heroOrb1} />
          <Box className={classes.heroOrb2} />
          <Typography className={classes.heroWatermark} aria-hidden>
            BISHOP
          </Typography>

          <Box className={classes.heroInner}>
            <Box className={classes.heroPill}>
              <Box className={classes.heroPillDot} />
              <Typography className={classes.heroPillText}>
                STS has its own platform
              </Typography>
            </Box>

            <Typography className={classes.heroTitle}>BISHOP</Typography>

            <Typography className={classes.heroTagline}>
              Backstage Infrastructure Self-service Hub for Operations &amp; Provisioning
            </Typography>

            <Typography className={classes.heroBody}>
              BISHOP is a self-service platform that lets the Infrastructure team
              define standardized, repeatable workflows for common tasks—provisioning,
              patching, and operations—that developers can trigger on demand.
            </Typography>

            <Box className={classes.heroCtaWrap}>
              <Button
                className={classes.heroCta}
                variant="contained"
                href={BISHOP_URL}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon style={{ fontSize: 15 }} />}
              >
                Open BISHOP
              </Button>
              <Typography className={classes.heroCtaNote} component="span">
                <VpnLockIcon style={{ fontSize: 14 }} />
                Note: VPN required
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── How AICOE Marketplace Fits ──────────────────────────── */}
        <Box className={classes.section}>
          <Box className={classes.sectionTop}>
            <Typography className={classes.sectionLabel}>
              How AICOE Marketplace Fits
            </Typography>
            <Box className={classes.sectionRule} />
          </Box>

          <Grid container spacing={3}>
            {FOUNDATION_ITEMS.map(item => {
              const Icon = item.icon;
              return (
                <Grid item xs={12} sm={6} key={item.title}>
                  <Box className={classes.foundationCard}>
                    <Typography
                      aria-hidden
                      className={classes.foundationGhostNum}
                    >
                      {item.num}
                    </Typography>
                    <Box className={classes.foundationIconWrap}>
                      <Icon />
                    </Box>
                    <Typography className={classes.foundationTitle}>
                      {item.title}
                    </Typography>
                    <Typography className={classes.foundationDesc}>
                      {item.description}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* ── Capabilities ────────────────────────────────────────── */}
        <Box className={classes.section}>
          <Box className={classes.sectionTop}>
            <Typography className={classes.sectionLabel}>
              Capabilities
            </Typography>
            <Box className={classes.sectionRule} />
          </Box>

          <Grid container spacing={3}>
            {CAPABILITIES.map(cap => {
              const Icon = cap.icon;
              return (
                <Grid item xs={12} sm={6} md={4} key={cap.title}>
                  <Box className={classes.capabilityCard}>
                    <Box className={classes.capabilityHeader}>
                      <Box className={classes.capabilityIconWrap}>
                        <Icon />
                      </Box>
                      <Typography className={classes.capabilityTitle}>
                        {cap.title}
                        {'badge' in cap && cap.badge && (
                          <span className={classes.badge}>{cap.badge}</span>
                        )}
                      </Typography>
                    </Box>
                    <ul className={classes.capabilityList}>
                      {cap.items.map(item => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* ── Access Management ───────────────────────────────────── */}
        <Box className={classes.section}>
          <Box className={classes.sectionTop}>
            <Typography className={classes.sectionLabel}>
              Access Management
            </Typography>
            <Box className={classes.sectionRule} />
          </Box>

          <Typography className={classes.accessNote}>
            Access is controlled through Okta groups:
          </Typography>

          <Grid container spacing={3}>
            {ACCESS_GROUPS.map(role => (
              <Grid item xs={12} sm={4} key={role.group}>
                <Box className={classes.accessCard}>
                  <Box className={classes.accessCardTop}>
                    <Typography className={classes.accessRole}>
                      {role.label}
                    </Typography>
                    <Typography
                      component="span"
                      className={classes.accessGroupCode}
                    >
                      {role.group}
                    </Typography>
                  </Box>
                  <Box className={classes.accessCardBottom}>
                    <Typography className={classes.accessDesc}>
                      {role.description}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ── Getting Started ─────────────────────────────────────── */}
        <Box className={classes.section}>
          <Box className={classes.sectionTop}>
            <Typography className={classes.sectionLabel}>
              Getting Started
            </Typography>
            <Box className={classes.sectionRule} />
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box className={classes.footerCard}>
                <Box className={classes.footerIconWrap}>
                  <PublishIcon />
                </Box>
                <Box className={classes.footerContent}>
                  <Typography className={classes.footerTitle}>
                    Onboard Your Template
                  </Typography>
                  <Typography className={classes.footerText}>
                    Click <strong>Create</strong> in the top navbar, paste your{' '}
                    <code>template.yaml</code> Bitbucket URL, click{' '}
                    <strong>Analyze</strong>, then <strong>Import</strong>.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box className={classes.footerCard}>
                <Box className={classes.footerIconWrap}>
                  <HelpOutlineIcon />
                </Box>
                <Box className={classes.footerContent}>
                  <Typography className={classes.footerTitle}>
                    Need Help?
                  </Typography>
                  <Typography className={classes.footerText}>
                    Contact the team via <strong>Contact Us</strong> for
                    onboarding, documentation issues, or portal enhancements.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

      </Content>
    </Page>
  );
};
